import { createTRPCReact } from '@trpc/react-query';
import {
  createTRPCClient,
  httpLink,
  httpBatchLink,
  splitLink,
  unstable_httpSubscriptionLink,
} from '@trpc/client';
import type { AppRouter } from '@perdiem/trpc';
import { QueryClient } from '@tanstack/react-query';
import { Platform } from 'react-native';
import { initializeSupabaseAuth, supabase } from './supabase';
import { resolveDevelopmentUrl } from './dev-url';

// React Native doesn't provide a global EventSource.  Import a fetch-based
// polyfill so tRPC's SSE subscription link can work on iOS/Android.
// On web the native EventSource is available, so we only need the polyfill for
// non-web platforms.
const getNativeEventSource = () => {
  if (Platform.OS === 'web') return undefined; // use globalThis.EventSource
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { EventSourcePolyfill } = require('event-source-polyfill');
  return EventSourcePolyfill as typeof EventSource;
};

export const trpc = createTRPCReact<AppRouter>();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 120_000,
      gcTime: 1_800_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const getApiUrl = () => {
  const url = process.env['EXPO_PUBLIC_API_URL'] ?? 'http://localhost:3001';
  return resolveDevelopmentUrl(url).replace(/\/$/, '');
};

/** Get the current Supabase session token for the Authorization header. */
async function getAuthToken(): Promise<string | null> {
  await initializeSupabaseAuth();
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export function createTRPCClientInstance() {
  const apiUrl = `${getApiUrl()}/trpc`;
  const authHeaders = async () => {
    const token = await getAuthToken();
    return token ? { authorization: `Bearer ${token}` } : {};
  };

  return trpc.createClient({
    links: [
      splitLink({
        /**
         * Subscriptions use HTTP SSE (server-sent events) via the Hono
         * fetchRequestHandler — no separate WebSocket server needed.
         * Native uses plain HTTP to avoid batched response parsing issues on
         * some device/runtime combinations. Web keeps batching enabled.
         */
        condition: (op) => op.type === 'subscription',
        true: unstable_httpSubscriptionLink({
          url: apiUrl,
          // Provide the polyfill so SSE works on React Native (iOS/Android)
          ...(Platform.OS !== 'web'
            ? { EventSource: getNativeEventSource() as unknown as typeof EventSource }
            : {}),
          connectionParams: async () => {
            const token = await getAuthToken();
            return token ? { token } : {};
          },
        }),
        false:
          Platform.OS === 'web'
            ? httpBatchLink({
                url: apiUrl,
                headers: authHeaders,
                maxItems: 8,
              })
            : httpLink({
                url: apiUrl,
                headers: authHeaders,
              }),
      }),
    ],
  });
}
